import { useEffect, useMemo, useState } from "react";
import type { Approval, CaseDetail, Company } from "@rma-orbit/shared";

import { api } from "../api/client.js";
import { navigate } from "../App.js";
import { agentGet, agentPost } from "../lib/agent.js";
import { copyRichText } from "../lib/clipboard.js";
import { Busy, ErrorNotice, Notice, StatusBadge, useAsync } from "../lib/ui.js";

/**
 * 택배 송장 — 로젠택배 (https://logis.ilogen.com/).
 *
 * 흐름: 승인은 났는데 아직 안 나간 건을 고른다 → 받는 곳 정보를 로젠 양식 순서로 만든다 →
 * [로젠 열기] 가 작업PC 에 로젠 창을 띄우고 양식 텍스트를 복사해 둔다 → 창에서 붙여넣고 발급 →
 * 나온 송장번호를 여기 적으면 출고가 등록된다(승인 잠금 그대로) → 송장 추적 링크가 붙는다.
 *
 * ★ 송장번호를 지어내지 않는다. 로젠이 발급한 번호를 사람이 옮겨 적는다(SOUL S2). 로젠 사이트에
 *   폼을 대신 제출하는 코드는 없다 — 그 사이트의 화면 원문을 저장한 적이 없어서 만들면 추측이다.
 */

const LOGEN = "https://logis.ilogen.com/";
const TRACE = (no: string) => `https://www.ilogen.com/web/personal/trace/${encodeURIComponent(no.replace(/\D/g, ""))}`;

interface Candidate {
  detail: CaseDetail;
  approval: Approval;
}

export function Shipping({ can }: { can: (p: string) => boolean }) {
  const companies = useAsync(() => api.companies(), []);
  const cases = useAsync(() => api.cases({ limit: 200 }), []);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [picked, setPicked] = useState<Candidate | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [agentUp, setAgentUp] = useState(false);

  useEffect(() => {
    agentGet().then(() => setAgentUp(true)).catch(() => setAgentUp(false));
  }, []);

  // 승인은 APPROVED 인데 그 방향 출고가 아직 없는 건 = 송장을 붙일 차례
  useEffect(() => {
    if (!cases.data) return;
    let alive = true;
    (async () => {
      const out: Candidate[] = [];
      const open = cases.data!.items.filter((c) => c.status !== "RETURNED");
      for (const c of open.slice(0, 80)) {
        try {
          const ap = await api.approvalsOfCase(c.id);
          const approved = ap.items.filter((a) => a.decision === "APPROVED");
          for (const a of approved) {
            const alreadyOut =
              (a.kind === "SHIP_TO_VENDOR" && c.status === "AT_VENDOR") ||
              (a.kind === "SHIP_TO_OWNER" && c.shipped_at !== null && c.status === "RETURNED");
            if (!alreadyOut) out.push({ detail: c, approval: a });
          }
        } catch {
          /* 스코프 밖 */
        }
      }
      if (alive) setCandidates(out);
    })();
    return () => {
      alive = false;
    };
  }, [cases.data]);

  return (
    <>
      <div className="admin-head">
        <span className="navicon" aria-hidden="true">✈</span>
        <div>
          <h1>택배 송장</h1>
          <p className="page-desc" style={{ margin: 0 }}>
            로젠택배로 나갑니다. 승인된 건을 고르면 로젠 양식대로 받는 곳을 만들어 주고, 발급된 송장번호를 적으면 출고가
            등록됩니다.
          </p>
        </div>
      </div>
      <ErrorNotice error={error ?? cases.error} />

      <div className="ship-layout">
        <div className="card" style={{ marginBottom: 0 }}>
          <h2>
            송장 붙일 차례<span className="sub">승인은 났는데 아직 안 나간 건</span>
          </h2>
          {candidates === null ? (
            <Busy>승인 상태를 읽는 중…</Busy>
          ) : candidates.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>지금은 없습니다. 케이스 화면에서 승인을 먼저 받으세요.</p>
          ) : (
            <div className="tablewrap">
              <table>
                <thead>
                  <tr><th>번호</th><th>시리얼</th><th>상태</th><th>방향</th><th /></tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={`${c.detail.id}-${c.approval.id}`} className={picked?.approval.id === c.approval.id ? "picked" : ""}>
                      <td><a href={`#/cases/${c.detail.id}`} onClick={(e) => { e.preventDefault(); navigate(`#/cases/${c.detail.id}`); }}>{c.detail.case_no}</a></td>
                      <td className="mono">{c.detail.serial_norm}</td>
                      <td><StatusBadge status={c.detail.status} /></td>
                      <td><span className="badge">{c.approval.kind === "SHIP_TO_VENDOR" ? "SECUI행" : "협력사행"}</span></td>
                      <td><button className={picked?.approval.id === c.approval.id ? "primary" : ""} onClick={() => setPicked(c)}>{picked?.approval.id === c.approval.id ? "선택됨" : "송장 붙이기"}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {picked ? (
          <LogenPanel
            key={picked.approval.id}
            candidate={picked}
            companies={companies.data ?? []}
            agentUp={agentUp}
            canShip={can("shipment.write")}
            onError={setError}
            onDone={() => {
              setPicked(null);
              cases.reload();
            }}
          />
        ) : (
          <div className="card logen-card" style={{ marginBottom: 0 }}>
            <div className="logen-head">
              <span className="logen-logo">LOGEN</span>
              <span className="muted">로젠택배 송장 자동 작성</span>
            </div>
            <ol className="ship-steps">
              <li><span className="n">1</span>왼쪽에서 건을 고릅니다</li>
              <li><span className="n">2</span>받는 곳을 로젠 양식 순서로 만들어 복사해 둡니다</li>
              <li><span className="n">3</span>로젠 창에서 붙여넣고 발급합니다</li>
              <li><span className="n">4</span>송장번호를 적으면 출고 등록 + 추적 링크</li>
            </ol>
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>
              받는 곳 정보를 로젠 양식으로 자동 작성해 창을 열고, 발급된 송장번호를 케이스에 기록합니다.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function LogenPanel({
  candidate,
  companies,
  agentUp,
  canShip,
  onError,
  onDone,
}: {
  candidate: Candidate;
  companies: Company[];
  agentUp: boolean;
  canShip: boolean;
  onError: (e: unknown) => void;
  onDone: () => void;
}) {
  const { detail, approval } = candidate;
  const direction = approval.kind === "SHIP_TO_VENDOR" ? "OUT_VENDOR" : "OUT_OWNER";
  const defaultDest = useMemo(() => {
    if (direction === "OUT_VENDOR") return companies.find((c) => c.kind === "VENDOR")?.id ?? 0;
    return detail.owner_company_id;
  }, [companies, direction, detail.owner_company_id]);
  const [destId, setDestId] = useState<number>(defaultDest);
  useEffect(() => setDestId(defaultDest), [defaultDest]);
  const dest = companies.find((c) => c.id === destId);

  const [tracking, setTracking] = useState("");
  const [shippedAt, setShippedAt] = useState(() => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" }).format(new Date()));
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<"form" | "opened" | "done">("form");
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const form = [
    `받는 분: ${dest?.name ?? ""}${detail.sender_name ? ` (${detail.sender_name})` : ""}`,
    `연락처: ${detail.sender_contact ?? ""}`,
    `주소: ${dest?.contact ?? ""}`,
    `품명: 방화벽 NGF-800 ED 1대`,
    `시리얼: ${detail.serial_norm}`,
    `참조: ${detail.case_no} · ${direction === "OUT_VENDOR" ? "SECUI 반납" : "협력사 반송"}`,
  ].join("\n");

  async function copyForm() {
    await copyRichText(`<pre>${form}</pre>`, form);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  async function openLogen() {
    onError(null);
    await copyForm();
    if (agentUp) {
      try {
        await agentPost("/window/open", { url: LOGEN, title: `로젠 송장 — ${detail.case_no}` });
      } catch (err) {
        onError(err);
        window.open(LOGEN, "_blank", "noopener");
      }
    } else {
      window.open(LOGEN, "_blank", "noopener");
    }
    setPhase("opened");
  }

  async function register() {
    onError(null);
    if (!tracking.trim() || !dest) return;
    setBusy(true);
    try {
      const sh = await api.createShipment({
        case_id: detail.id,
        direction,
        dest_company_id: dest.id,
        shipped_at: shippedAt,
        carrier: "로젠택배",
        tracking: tracking.trim(),
        approval_id: approval.id,
      });
      setSaved(`출고 ${sh.id}번으로 등록했습니다. 송장 ${tracking.trim()}`);
      setPhase("done");
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card logen-card" style={{ marginBottom: 0 }}>
      <div className="logen-head">
        <span className="logen-logo">LOGEN</span>
        <span className="muted">{detail.case_no} · {direction === "OUT_VENDOR" ? "SECUI 반납" : "협력사 반송"}</span>
      </div>

      <ol className="ship-steps">
        <li className={phase !== "form" ? "done" : "now"}><span className="n">1</span>받는 곳
          <div className="row" style={{ marginTop: 6 }}>
            <select value={destId} onChange={(e) => setDestId(Number(e.target.value))}>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}{c.kind === "VENDOR" ? " (SECUI)" : ""}</option>)}
            </select>
          </div>
        </li>
        <li className={phase === "opened" ? "now" : phase === "done" ? "done" : ""}><span className="n">2</span>로젠 양식으로 복사해 창 열기
          <pre className="mono logen-form">{form}</pre>
          <div className="row" style={{ marginTop: 6 }}>
            <button className="primary" onClick={() => void openLogen()}>{agentUp ? "로젠 창 열기 (양식 복사됨)" : "로젠 새 탭 열기 (양식 복사됨)"}</button>
            <button onClick={() => void copyForm()}>{copied ? "복사됨" : "양식만 복사"}</button>
          </div>
          <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
            {agentUp ? "작업PC 에 로젠 창이 뜹니다. 붙여넣고 발급하세요." : "수집기가 꺼져 있어 새 탭으로 엽니다."}
          </p>
        </li>
        <li className={phase === "done" ? "done" : phase === "opened" ? "now" : ""}><span className="n">3</span>발급된 송장번호 적기
          <div className="row" style={{ marginTop: 6, alignItems: "flex-end" }}>
            <label className="field">송장번호<input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="451-0477-4400" style={{ width: 160 }} /></label>
            <label className="field">출고일<input type="date" value={shippedAt} onChange={(e) => setShippedAt(e.target.value)} /></label>
            <button className="primary" disabled={!canShip || busy || !tracking.trim() || !dest} onClick={() => void register()}>{busy ? "등록 중…" : "출고 등록"}</button>
          </div>
          {!canShip && <p className="muted" style={{ fontSize: 12 }}>출고 등록 권한(shipment.write)이 필요합니다.</p>}
        </li>
        <li className={phase === "done" ? "now" : ""}><span className="n">4</span>추적
          {tracking.trim() ? (
            <div style={{ marginTop: 6 }}>
              <a href={TRACE(tracking)} target="_blank" rel="noreferrer">로젠 배송 조회 — {tracking.trim()}</a>
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>송장번호를 적으면 조회 링크가 생깁니다.</div>
          )}
        </li>
      </ol>

      {saved && (
        <Notice kind="ok">
          {saved}{" "}
          <button className="ghost" onClick={onDone}>다음 건</button>
        </Notice>
      )}
    </div>
  );
}
